import { Link } from "react-router-dom";

const LAST_UPDATED = "13 July 2026";
const CONTACT_EMAIL = "support@ordeneli.com";
const SERVICE_NAME = "Ordeneli";

function LegalShell({ eyebrow, title, intro, children }) {
  return (
    <div className="bg-white">
      <section className="border-b border-[#E5E7EB]">
        <div className="container-wide py-10 md:py-14">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">
            {eyebrow}
          </p>

          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3">
            {title}
          </h1>

          <p className="mt-4 text-[#525252] text-sm sm:text-base max-w-3xl leading-relaxed">
            {intro}
          </p>

          <p className="mt-4 font-mono text-xs uppercase tracking-[0.18em] text-[#737373]">
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      <section className="container-wide py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-3">
            <div className="border border-[#E5E7EB] bg-[#F5F5F5] p-5 sticky top-24">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#737373]">
                Legal pages
              </p>

              <nav className="mt-4 grid gap-2 text-sm font-semibold">
                <Link to="/privacy" className="hover:text-[#002FA7]">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="hover:text-[#002FA7]">
                  Terms of Service
                </Link>
                <Link to="/dpa" className="hover:text-[#002FA7]">
                  Data Processing Addendum
                </Link>
                <Link to="/cookies" className="hover:text-[#002FA7]">
                  Cookie Policy
                </Link>
              </nav>

              <div className="mt-6 border-t border-[#E5E7EB] pt-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">
                  Contact
                </p>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="mt-2 block text-sm font-semibold text-[#002FA7] break-all"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-9">
            <div className="border border-[#E5E7EB] bg-white p-6 sm:p-8 md:p-10">
              <div className="space-y-8 text-[#333] leading-relaxed">
                {children}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-black text-[#0A0A0A] tracking-tight">
        {title}
      </h2>
      <div className="mt-3 space-y-3 text-sm sm:text-base text-[#525252]">
        {children}
      </div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-2">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function SmallNotice({ children }) {
  return (
    <div className="border border-[#BFDBFE] bg-[#EFF6FF] p-4 text-sm text-[#1E40AF]">
      {children}
    </div>
  );
}

export function Privacy() {
  return (
    <LegalShell
      eyebrow="Privacy"
      title="Privacy Policy"
      intro="This Privacy Policy explains how Ordeneli collects, uses, stores and protects personal data when you use our website, AI tools, workflows, integrations and publishing features."
    >
      <SmallNotice>
        This policy is written for a UK SaaS product and covers account data, project data,
        AI inputs/outputs, uploaded media, connected accounts, publishing logs and payment records.
      </SmallNotice>

      <Section title="1. Who we are">
        <p>
          {SERVICE_NAME} is a marketing automation and AI content workflow platform operated from
          the United Kingdom. In this policy, “Ordeneli”, “we”, “us” and “our” refer to the operator
          of the Ordeneli service available at ordeneli.com and related subdomains.
        </p>
        <p>
          For privacy, data protection or GDPR questions, contact{" "}
          <a className="text-[#002FA7] font-semibold" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </Section>

      <Section title="2. Roles under data protection law">
        <p>
          For account, billing, security, product usage and support data, Ordeneli normally acts as
          a controller.
        </p>
        <p>
          For personal data that customers upload, generate, store or publish through projects,
          workflows, integrations and connected accounts, the customer may act as controller and
          Ordeneli may act as processor. In that case, our{" "}
          <Link className="text-[#002FA7] font-semibold" to="/dpa">
            Data Processing Addendum
          </Link>{" "}
          applies.
        </p>
      </Section>

      <Section title="3. Personal data we collect">
        <p>We may collect and process:</p>
        <BulletList
          items={[
            "Account data: name, email address, password hash, sign-in method, profile image, workspace and plan details.",
            "Project data: project names, domains, target locations, descriptions, brand details, workflow settings and team information.",
            "Content data: prompts, AI inputs, generated outputs, draft posts, uploaded media, approvals, scheduled content and published content metadata.",
            "Integration data: connected account names, provider IDs, scopes, encrypted tokens, status, token expiry and publishing destination details.",
            "Usage and audit data: generations, plan limits, API key usage, publishing logs, error logs, security logs, moderation events and abuse-prevention signals.",
            "Billing data: subscription plan, payment status, transaction identifiers, checkout/portal references and invoice-related information handled by our payment provider.",
            "Support data: emails and messages you send to us.",
          ]}
        />
      </Section>

      <Section title="4. Purposes and lawful bases">
        <p>We use personal data for the following purposes:</p>
        <BulletList
          items={[
            "To provide the service, manage accounts, authenticate users and operate workspaces. Lawful basis: contract.",
            "To generate AI outputs, run workflows, schedule posts and publish to connected services when requested. Lawful basis: contract and, where customer content contains personal data, processor instructions under the DPA.",
            "To process payments, manage plans and keep business records. Lawful basis: contract and legal obligation.",
            "To protect the platform, prevent abuse, investigate unsafe uploads and maintain logs. Lawful basis: legitimate interests and legal obligation where applicable.",
            "To send essential service emails such as password resets, invites and important account notices. Lawful basis: contract or legitimate interests.",
            "To improve reliability, diagnose bugs and understand usage. Lawful basis: legitimate interests, or consent where required for optional cookies/analytics.",
          ]}
        />
      </Section>

      <Section title="5. AI content and sensitive data">
        <p>
          Ordeneli uses AI providers to generate and transform content based on inputs you provide.
          Do not submit sensitive personal data, confidential business information, special category
          data or third-party personal data unless you have the right and lawful basis to do so.
        </p>
        <p>
          You are responsible for reviewing AI outputs before using, publishing or relying on them.
          AI outputs may be inaccurate, incomplete or unsuitable.
        </p>
      </Section>

      <Section title="6. Connected platforms">
        <p>
          If you connect LinkedIn Pages, WordPress or another third-party service, we use the
          permissions you grant to perform actions you request, such as listing pages, creating
          drafts, uploading images, scheduling content or publishing posts.
        </p>
        <p>
          Third-party platforms have their own terms, privacy policies and API rules. You are
          responsible for making sure you have permission to connect and publish to those accounts.
        </p>
      </Section>

      <Section title="7. Sharing personal data">
        <p>We may share data with:</p>
        <BulletList
          items={[
            "Hosting, database, logging and infrastructure providers.",
            "AI service providers used to process prompts and generate outputs.",
            "Payment providers used for checkout, subscriptions and billing support.",
            "Email providers used for password resets, invites and service messages.",
            "Third-party platforms you connect, only as needed to perform requested actions.",
            "Professional advisers, regulators, law enforcement or courts where required by law or needed to protect our rights.",
          ]}
        />
        <p>We do not sell your personal data to advertisers.</p>
      </Section>

      <Section title="8. International transfers">
        <p>
          Some providers may process data outside the UK. Where required, we use appropriate
          safeguards such as contractual protections or providers that offer recognised transfer
          mechanisms.
        </p>
      </Section>

      <Section title="9. Retention">
        <p>
          We keep data only for as long as reasonably needed to provide the service, meet legal and
          accounting obligations, maintain security, resolve disputes and enforce our terms.
        </p>
        <p>
          Account and workspace data is generally kept while your account is active. Some logs,
          billing records, audit records and security records may be kept for longer where needed.
        </p>
      </Section>

      <Section title="10. Security">
        <p>
          We use reasonable technical and organisational measures designed to protect data, including
          authentication, access controls, encrypted integration-token storage where supported,
          logging and moderation/abuse-prevention controls.
        </p>
        <p>
          No online service can be guaranteed completely secure. You must keep passwords, API keys
          and connected-account access private.
        </p>
      </Section>

      <Section title="11. Your rights">
        <p>
          Depending on applicable law, you may have rights to access, correct, delete, restrict,
          object to processing, request portability and withdraw consent where consent is used.
        </p>
        <p>
          Contact{" "}
          <a className="text-[#002FA7] font-semibold" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>{" "}
          to exercise rights. You can also complain to the UK Information Commissioner’s Office.
        </p>
      </Section>

      <Section title="12. Changes">
        <p>
          We may update this policy as Ordeneli changes. The date at the top shows the latest update.
        </p>
      </Section>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell
      eyebrow="Terms"
      title="Terms of Service"
      intro="These Terms of Service explain the rules for using Ordeneli, including payment terms, refunds, acceptable use, AI content, integrations and liability limits."
    >
      <SmallNotice>
        Important: these terms help protect Ordeneli, but no website terms can stop every dispute.
        For a final launch, have a UK solicitor check the wording against your final business entity,
        Stripe/payment setup and customer type.
      </SmallNotice>

      <Section title="1. Agreement">
        <p>
          By creating an account, starting a subscription, connecting an integration or using
          Ordeneli, you agree to these Terms of Service, our Privacy Policy, Cookie Policy and, where
          applicable, our Data Processing Addendum.
        </p>
        <p>
          If you use Ordeneli for a business, company, organisation or client, you confirm that you
          have authority to accept these terms on their behalf.
        </p>
      </Section>

      <Section title="2. The service">
        <p>
          Ordeneli provides AI marketing tools, project context, workflow automation, approvals,
          content generation, scheduling, publishing, integrations and related features.
        </p>
        <p>
          We may update, improve, limit, suspend, replace or remove features where needed for product,
          security, legal, technical or commercial reasons.
        </p>
      </Section>

      <Section title="3. Accounts">
        <p>
          You must provide accurate account information and keep login details, API keys and
          connected-account access secure. You are responsible for all activity under your account,
          workspace, team members and integrations.
        </p>
      </Section>

      <Section title="4. Subscriptions, payment and renewals">
        <p>
          Paid plans are billed according to the plan, price and billing period shown at checkout.
          Plan limits may include generations, project slots, team seats, API keys, workflow features,
          connected accounts and publishing capacity.
        </p>
        <p>
          Unless cancelled before the next billing date, subscriptions may renew automatically. If a
          payment fails, we may suspend or limit paid features until payment is made.
        </p>
      </Section>

      <Section title="5. Refunds and cancellation">
        <p>
          You can cancel your subscription for the future through the billing portal where available
          or by contacting{" "}
          <a className="text-[#002FA7] font-semibold" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
        <p>
          Except where required by law or expressly agreed by us in writing, fees already paid are
          non-refundable, including where you decide you no longer want the service, forgot to cancel,
          did not use your allowance, or dislike an AI-generated result.
        </p>
        <p>
          If you are a consumer, your statutory rights are not affected. Where consumer cancellation
          rights apply, you may have a legal cooling-off period. However, where you request immediate
          access to digital services/content and the service has begun, your right to cancel or the
          amount refundable may be limited as allowed by law.
        </p>
        <p>
          We may still offer goodwill credits or refunds at our discretion, but doing so once does not
          create an obligation to do so again.
        </p>
      </Section>

      <Section title="6. Your content and permissions">
        <p>
          You keep ownership of content, prompts, files, media, project descriptions and connected
          account content you submit. You grant Ordeneli permission to process that content to provide
          the service, including generating AI outputs, storing drafts, scheduling posts and
          publishing when you request it.
        </p>
        <p>
          You confirm that you have all rights, permissions and lawful bases needed for content you
          upload, generate, approve, schedule, publish or connect through Ordeneli.
        </p>
      </Section>

      <Section title="7. AI-generated output">
        <p>
          AI outputs may be wrong, incomplete, similar to other outputs, non-compliant with platform
          rules, or unsuitable for your specific use. You must review, edit and approve outputs before
          publishing, sending, relying on or using them commercially.
        </p>
        <p>
          Ordeneli does not provide legal, financial, medical, tax, employment or regulatory advice.
          You are responsible for checking that any published content is accurate, lawful and
          appropriate for your business.
        </p>
      </Section>

      <Section title="8. Third-party integrations and publishing">
        <p>
          You are responsible for complying with the terms, policies and API rules of third-party
          platforms you connect, including LinkedIn, WordPress and any other publishing destination.
        </p>
        <p>
          We are not responsible for third-party outages, API changes, token expiry, rate limits,
          moderation decisions, account restrictions, rejected posts, deleted posts or publishing
          failures caused by external platforms.
        </p>
      </Section>

      <Section title="9. No unauthorised testing, scraping or attacks">
        <p>
          You must not test, scan, probe, benchmark, stress test, load test, scrape, crawl, reverse
          engineer, attack or attempt to bypass Ordeneli’s systems, security, rate limits, payment
          controls, moderation controls, plan limits or access controls without our prior written
          permission.
        </p>
        <p>
          This includes automated vulnerability scans, penetration tests, fake traffic, bot usage,
          credential stuffing, SQL injection testing, XSS testing, API abuse, scraping content,
          bypassing upload checks, testing stolen data, testing malware, or using Ordeneli to test
          harmful/unlawful content.
        </p>
        <p>
          If you believe you found a security issue, contact{" "}
          <a className="text-[#002FA7] font-semibold" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>{" "}
          and do not access, copy, modify, delete, publish or disclose data that is not yours.
        </p>
      </Section>

      <Section title="10. Acceptable use">
        <p>You must not use Ordeneli to:</p>
        <BulletList
          items={[
            "Break the law, infringe rights or violate third-party platform rules.",
            "Create, upload, publish or schedule unlawful, harmful, abusive, hateful, deceptive, exploitative, sexual, violent, extremist, defamatory or infringing content.",
            "Send spam, phishing, scams, impersonation, malware or unauthorised marketing.",
            "Upload or process personal data without a lawful basis or proper authority.",
            "Publish through accounts, pages, websites or brands you do not own or control.",
            "Misrepresent AI-generated content as professional advice or guaranteed results.",
            "Resell, copy, clone, frame or white-label Ordeneli without written permission.",
            "Use the service to train competing models, copy product features or create a substantially similar service.",
          ]}
        />
      </Section>

      <Section title="11. Suspension and termination">
        <p>
          We may suspend, restrict or terminate access if we reasonably believe you breached these
          terms, created risk, failed to pay, abused the service, connected accounts without
          authority, attempted unauthorised testing, or violated platform rules.
        </p>
        <p>
          We may remove or block content, integrations, uploads or publishing activity where needed
          for safety, legal, security or compliance reasons.
        </p>
      </Section>

      <Section title="12. Data protection">
        <p>
          Our Privacy Policy explains how we handle personal data. Where we process customer personal
          data as a processor, the Data Processing Addendum applies.
        </p>
      </Section>

      <Section title="13. Intellectual property">
        <p>
          Ordeneli, including its software, design, branding, workflows, platform structure, UI,
          documentation and underlying systems, belongs to us or our licensors. You may not copy,
          reproduce, sell or create derivative products from Ordeneli except as allowed by these terms.
        </p>
      </Section>

      <Section title="14. Disclaimers">
        <p>
          Ordeneli is provided on an “as is” and “as available” basis. We do not guarantee that the
          service will be uninterrupted, error-free, secure, compatible with every platform, or that
          AI outputs will achieve a specific result, ranking, revenue, engagement level or compliance
          outcome.
        </p>
      </Section>

      <Section title="15. Liability limit">
        <p>
          To the fullest extent permitted by law, we are not liable for indirect, consequential or
          special losses, including lost profits, lost revenue, loss of goodwill, loss of data,
          business interruption, reputational damage, failed publishing, third-party platform actions
          or reliance on AI outputs.
        </p>
        <p>
          To the fullest extent permitted by law, our total liability for claims relating to the
          service is limited to the amount you paid to Ordeneli for the service in the three months
          before the event giving rise to the claim.
        </p>
        <p>
          Nothing in these terms excludes liability that cannot legally be excluded, including death
          or personal injury caused by negligence, fraud or fraudulent misrepresentation.
        </p>
      </Section>

      <Section title="16. Indemnity">
        <p>
          If you use Ordeneli for business purposes, you agree to indemnify and hold us harmless from
          claims, losses, damages, fines, costs and expenses arising from your content, unlawful use,
          breach of these terms, misuse of third-party platforms, unauthorised publishing, or
          processing of personal data without proper rights or lawful basis.
        </p>
      </Section>

      <Section title="17. Governing law">
        <p>
          These terms are governed by the laws of England and Wales, unless mandatory consumer laws
          require otherwise.
        </p>
      </Section>

      <Section title="18. Contact">
        <p>
          Questions about these terms can be sent to{" "}
          <a className="text-[#002FA7] font-semibold" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </Section>
    </LegalShell>
  );
}

export function DPA() {
  return (
    <LegalShell
      eyebrow="Data protection"
      title="Data Processing Addendum"
      intro="This Data Processing Addendum explains how Ordeneli processes customer personal data when acting as a processor on behalf of a customer."
    >
      <SmallNotice>
        This DPA is intended for business customers using Ordeneli to process personal data inside
        projects, workflows, AI prompts, drafts, approvals, uploads, integrations or publishing logs.
      </SmallNotice>

      <Section title="1. Parties and scope">
        <p>
          This Data Processing Addendum forms part of the Terms of Service between the customer
          (“Customer”, “controller”) and Ordeneli (“processor”) where Ordeneli processes Customer
          Personal Data on behalf of the Customer.
        </p>
        <p>
          “Customer Personal Data” means personal data submitted to Ordeneli by or on behalf of the
          Customer through the service, excluding account, billing, security and product usage data
          that Ordeneli processes as controller.
        </p>
      </Section>

      <Section title="2. Processing details">
        <p>The processing details are:</p>
        <BulletList
          items={[
            "Subject matter: provision of Ordeneli’s AI marketing, workflow, project, integration and publishing services.",
            "Duration: for the term of the customer’s use of the service and any retention period described in the Privacy Policy or required by law.",
            "Nature and purpose: hosting, storing, retrieving, generating, transforming, scheduling, publishing, logging, securing and supporting customer content and workflows.",
            "Types of personal data: names, email addresses, job titles, business contact details, uploaded content, prompts, generated content, social/page identifiers, website content and any personal data included by the customer in projects or workflows.",
            "Categories of data subjects: customer users, team members, clients, prospects, employees, contractors, website visitors, social media audiences and other individuals included in customer content.",
          ]}
        />
      </Section>

      <Section title="3. Customer instructions">
        <p>
          Ordeneli will process Customer Personal Data only on documented instructions from the
          Customer, including instructions given through the product interface, API, settings,
          workflows, integrations and support requests, unless required by law.
        </p>
      </Section>

      <Section title="4. Customer responsibilities">
        <p>The Customer is responsible for:</p>
        <BulletList
          items={[
            "Having a lawful basis for personal data submitted to Ordeneli.",
            "Providing required privacy notices to data subjects.",
            "Ensuring that users, team members and connected accounts are authorised.",
            "Reviewing AI outputs and published content.",
            "Avoiding unnecessary sensitive data or special category data unless legally permitted and appropriate safeguards are in place.",
          ]}
        />
      </Section>

      <Section title="5. Confidentiality">
        <p>
          Ordeneli will ensure that people authorised to process Customer Personal Data are subject
          to appropriate confidentiality obligations.
        </p>
      </Section>

      <Section title="6. Security measures">
        <p>
          Ordeneli will use appropriate technical and organisational measures designed to protect
          Customer Personal Data, taking into account the nature of the service, risk, available
          technology and cost of implementation.
        </p>
        <p>Measures may include authentication, access controls, encrypted token storage where supported, logging, backups, moderation controls, secure hosting and incident response procedures.</p>
      </Section>

      <Section title="7. Sub-processors">
        <p>
          Customer authorises Ordeneli to use sub-processors needed to provide the service, such as
          hosting, infrastructure, database, AI, email, payment, logging and connected-platform
          providers.
        </p>
        <p>
          Ordeneli will require sub-processors to protect Customer Personal Data under appropriate
          contractual obligations. A current sub-processor list can be requested by contacting{" "}
          <a className="text-[#002FA7] font-semibold" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </Section>

      <Section title="8. International transfers">
        <p>
          Where Customer Personal Data is transferred outside the UK or EEA, Ordeneli will use
          appropriate safeguards where required, such as recognised contractual transfer mechanisms
          or providers offering adequate protections.
        </p>
      </Section>

      <Section title="9. Assistance">
        <p>
          Taking into account the nature of processing and information available to us, Ordeneli will
          provide reasonable assistance with data subject requests, security obligations, data
          protection impact assessments and regulatory enquiries where required by applicable data
          protection law.
        </p>
      </Section>

      <Section title="10. Security incidents">
        <p>
          If Ordeneli becomes aware of a personal data breach affecting Customer Personal Data, we
          will notify the Customer without undue delay after becoming aware of it and provide
          reasonable information to help the Customer meet its legal obligations.
        </p>
      </Section>

      <Section title="11. Return or deletion">
        <p>
          On termination of the service, Ordeneli will delete or return Customer Personal Data where
          reasonably possible, unless retention is required by law or necessary for legitimate
          security, billing, dispute, audit or backup purposes.
        </p>
      </Section>

      <Section title="12. Audits">
        <p>
          Ordeneli will make available reasonable information necessary to demonstrate compliance
          with this DPA. Any audit or inspection must be reasonable, proportionate, subject to
          confidentiality, avoid disruption to the service and not compromise other customers’ data or
          platform security.
        </p>
      </Section>

      <Section title="13. Conflict">
        <p>
          If there is a conflict between this DPA and the Terms of Service regarding processing of
          Customer Personal Data, this DPA controls to the extent of that conflict.
        </p>
      </Section>
    </LegalShell>
  );
}

export function Cookies() {
  return (
    <LegalShell
      eyebrow="Cookies"
      title="Cookie Policy"
      intro="This Cookie Policy explains how Ordeneli uses cookies and similar technologies such as local storage, session storage, pixels and scripts."
    >
      <Section title="1. What cookies and similar technologies are">
        <p>
          Cookies are small files placed on your device. Similar technologies include local storage,
          session storage, pixels, SDKs and scripts that store or access information on your device.
        </p>
      </Section>

      <Section title="2. How we use them">
        <p>Ordeneli may use cookies or similar technologies for:</p>
        <BulletList
          items={[
            "Essential authentication, security and session management.",
            "Remembering product preferences and workspace settings.",
            "Checkout, billing portal and subscription flows.",
            "OAuth and connected-platform flows, including LinkedIn or WordPress redirects.",
            "Security logging, abuse prevention and fraud prevention.",
            "Analytics or performance measurement, if enabled and where legally permitted.",
          ]}
        />
      </Section>

      <Section title="3. Strictly necessary cookies">
        <p>
          Strictly necessary cookies and storage are required for login, security, routing, checkout,
          account access, OAuth connections and publishing features. These cannot usually be disabled
          without breaking the service.
        </p>
      </Section>

      <Section title="4. Optional cookies and consent">
        <p>
          If Ordeneli uses optional analytics, advertising, tracking or similar non-essential cookies,
          we will ask for consent where required before setting them.
        </p>
        <p>
          At the time of this policy, Ordeneli is intended to rely mainly on essential service cookies
          and storage. If analytics, advertising pixels or other non-essential tracking are added, a
          cookie consent control should be shown where required.
        </p>
      </Section>

      <Section title="5. Third-party cookies">
        <p>
          Third-party services may set their own cookies or similar technologies when you use payment,
          login, OAuth, embedded or connected-platform features. These third parties control their own
          technologies and policies.
        </p>
      </Section>

      <Section title="6. Managing cookies">
        <p>
          You can manage or delete cookies through your browser settings. Blocking essential cookies
          may stop parts of Ordeneli from working.
        </p>
      </Section>

      <Section title="7. Contact">
        <p>
          Cookie questions can be sent to{" "}
          <a className="text-[#002FA7] font-semibold" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </Section>
    </LegalShell>
  );
}
